import React from 'react';
import { ZOOM } from '@/constants';
import svgElementDraggableListener from '../../hoc/svgElementDraggableListener';
import svgElementClickableListener from '../../hoc/svgElementClickableListener';
import svgRectResizable from '../../hoc/svgRectResizable';

class CornerRenderer extends React.PureComponent {
  getAreaHints(width, height) {
    const isWidthDominant = width > height;

    return {
      smallArea: {
        key: isWidthDominant ? 'height' : 'width',
        value: isWidthDominant ? height : width
      },
      largeArea: {
        key: isWidthDominant ? 'width' : 'height',
        value: isWidthDominant ? width : height
      }
    }
  }

  getAreaSizes(area) {
    return area.reduce((acc, section) => {
      if(section.width) acc['width'].push(section.width);
      if(section.height) acc['height'].push(section.height);
      return acc;
    }, { width: [], height: [] });
  }

  getMinAreaSizes(area) {
    const areaSizes = this.getAreaSizes(area);
    return Object.values(areaSizes).map((size) => Math.min.apply(this, size));
  }

  getMaxAreaSizes(area) {
    const areaSizes = this.getAreaSizes(area);
    return Object.values(areaSizes).map((size) => Math.max.apply(this, size));
  }

  getAreaBox(area, isSquare) {
    const [ minAreaWidth, minAreaHeight ] = this.getMinAreaSizes(area);
    const { smallArea } = this.getAreaHints(minAreaWidth, minAreaHeight);

    const similarSelection = area.filter((section) => section[smallArea.key] === smallArea.value);

    return (isSquare ? area : similarSelection).reduce((acc, section) => {
      if(!acc['x'].includes(section.x)) {
        acc['x'].push(section.x);
        acc['width'] += section.width;
      }

      if(!acc['y'].includes(section.y)) {
        acc['y'].push(section.y);
        acc['height'] += section.height;
      }

      return acc;
    }, { width: 0, height: 0, x: [], y: [] });
  }

  calcAreaCoefficient(selectedSections, corners, maxCoefficient) {
    let coefficient = 1;
    let baseCornerPercentage = 0.3;
    let maxValue = maxCoefficient;

    const box = this.getAreaBox(selectedSections, corners === 4);
    const [minAreaWidth, minAreaHeight] = this.getMinAreaSizes(selectedSections);

    if(corners !== 4) {
      if(box.width < box.height) {
        coefficient = box.width > minAreaWidth ? minAreaWidth : box.width;
      } else {
        coefficient = box.height > minAreaHeight ? minAreaHeight : box.height;
      }
    } else {
      coefficient = box.width > box.height ? box.height : box.width;
    }

    coefficient *= baseCornerPercentage;

    return coefficient < maxValue ? coefficient : maxValue;
  }

  getShieldCoefficient(shield) {
    const { primitiveProps: { w, h } } = shield;
    const largeSide = w > h ? h : w;

    return largeSide / 10;
  }

  render() {
    const {
      id,
      rectProps,
      groupIdPostfix = '',
      scaleCoef = 1,

      shield,
      className,
      svgElementDraggableRef,
      svgElementClickableRef,

      isHidden,

      dx: defaultDx = 0,
      dy: defaultDy = 0,
      corner,

      selectedSections,
      quantity
    } = this.props;
    const isActive = true, dx = defaultDx / scaleCoef / ZOOM || 0, dy = defaultDy / scaleCoef / ZOOM || 0;
    const maxCoefficient = this.getShieldCoefficient(shield);
    const areaScaleCoef = this.calcAreaCoefficient(selectedSections, quantity, maxCoefficient);
    const { type, edge } = corner;

    return (
      <g
        id={`${id}-group${groupIdPostfix}`}
        style={{ cursor: isActive ? 'pointer' : 'default' }}
        className={className}
        transform={`translate(${dx}, ${dy})`}
        ref={el => {
          svgElementDraggableRef(el);
          svgElementClickableRef(el);
        }}
        data-value={`matrix-selection-corner-${type}-${id}`}
      >
        <g
          id={'matrixselection_corner_group_'+ id}
          className="matrixselection_corner"
          transform={'translate('+rectProps.x+','+rectProps.y+')'}
        >
          <path
            id={'matrixselection_corner_'+ id}
            className="matrixselection_corner_path"
            transform={`scale(${areaScaleCoef})`}
            stroke={isHidden? 'transparent' : '#ffffff'}
            fill={isHidden? 'transparent' : '#2097a0'}
            strokeWidth={0.1}
            d={edge}
          />
        </g>
      </g>
    );
  }
}

export default svgRectResizable(svgElementClickableListener(svgElementDraggableListener(CornerRenderer)));
